import {Inject, Injectable} from '@angular/core';
import {SuppliersService} from '../../../services/suppliers.service';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {IncidentsState} from './incidents.state';
import {IncidentsActions} from './incidents.index';
import {map, switchMap} from 'rxjs/operators';
import {CoreService, Param, Types} from '@proleis/proleis-rest-client';
import {APP_GUIDS, AppGuids} from '../../../app.guids';
import {CurrentViewService} from '@proleis/proleis-web-app';

@Injectable()
export class IncidentsEffects {
  constructor(private store: Store<IncidentsState>, private actions: Actions, private suppliersService: SuppliersService,
              private coreService: CoreService, @Inject(APP_GUIDS) private appGuids: AppGuids, private cvs: CurrentViewService) {
  }

  loadIncidents$ = createEffect(() => {
    return this.actions.pipe(
      ofType(IncidentsActions.LOAD_INCIDENTS),
      switchMap(action => {
        this.cvs.setLoading(true);
        return this.suppliersService.getIncidents(action.orderId, action.posId, action.aliasFields, action.zqlWhere).pipe(
          map(incidents => {
            this.cvs.setLoading(false);
            return IncidentsActions.LOAD_INCIDENTS_SUCCESS({
              incidents
            });
          })
        );
      })
    );
  });

  selectIncident$ = createEffect(() => {
    return this.actions.pipe(
      ofType(IncidentsActions.SELECT_INCIDENT),
      switchMap(action => {
        return this.suppliersService.getIncident(action.orderId, action.posId, action.incidentId, action.aliasFields).pipe(
          map(incident => IncidentsActions.SELECT_INCIDENT_SUCCESS({incident}))
        );
      })
    );
  });

  loadDocuments$ = createEffect(() => {
    return this.actions.pipe(
      ofType(IncidentsActions.LOAD_INCIDENT_DOCUMENTS),
      switchMap(action => {
        return this.suppliersService.getIncidentDocuments(action.orderId, action.posId, action.incidentId).pipe(
          map(documents => IncidentsActions.LOAD_INCIDENT_DOCUMENTS_SUCCESS({documents}))
        );
      })
    );
  });

  loadComments$ = createEffect(() => {
    return this.actions.pipe(
      ofType(IncidentsActions.LOAD_INCIDENT_COMMENTS),
      switchMap(action => {
        return this.suppliersService.getIncidentComments(action.orderId, action.posId, action.incidentId, action.aliasFields).pipe(
          map(comments => IncidentsActions.LOAD_INCIDENT_COMMENTS_SUCCESS({
            comments
          }))
        );
      })
    );
  });

  acceptIncident$ = createEffect(() => {
    return this.actions.pipe(
      ofType(IncidentsActions.ACCEPT_INCIDENT),
      switchMap(action => {
        return this.coreService.executeAction(action.incidentId, '§' + this.appGuids.GUID_SCRIPT_VORFALL_ACCEPT).pipe(
          map(result => IncidentsActions.ACCEPT_INCIDENT_SUCCESS({
            orderId: action.orderId,
            incidentId: action.incidentId,
            posId: action.posId,
            aliasFields: action.aliasFields
          }))
        );
      })
    );
  });

  acceptIncidentsSuccess$ = createEffect(() => {
    return this.actions.pipe(
      ofType(IncidentsActions.ACCEPT_INCIDENT_SUCCESS),
      map(action => IncidentsActions.LOAD_INCIDENTS({
        posId: action.posId,
        orderId: action.orderId,
        aliasFields: action.aliasFields
      }))
    );
  });

  addComment$ = createEffect(() => {
    return this.actions.pipe(
      ofType(IncidentsActions.ADD_COMMENT),
      switchMap(action => {
        const params: Param[] = [{
          name: 'COMMENT',
          value: action.comment,
          type: Types.TEXT
        }];
        return this.coreService.executeAction(action.incidentId, '§' + this.appGuids.GUID_ACTION_COMMENT, params).pipe(
          map(result => IncidentsActions.ADD_COMMENT_SUCCESS())
        );
      })
    );
  });

  acceptIncidentChange$ = createEffect(() => {
    return this.actions.pipe(
      ofType(IncidentsActions.INCIDENT_ACCEPT_CHANGE),
      switchMap(action => {
        const params: Param[] = [];
        return this.coreService.executeAction(action.incidentId, '§' + this.appGuids.GUID_ACTION_ACCEPT_CHANGE, params).pipe(
          map(result => IncidentsActions.INCIDENT_ACCEPT_CHANGE_SUCCESS({
            incidentId: action.incidentId,
            posId: action.posId,
            orderId: action.orderId,
            aliasFields: action.aliasFields
          }))
        );
      })
    );
  });

  acceptIncidentChangeSuccess$ = createEffect(() => {
    return this.actions.pipe(
      ofType(IncidentsActions.INCIDENT_ACCEPT_CHANGE_SUCCESS),
      map(action => {
        return IncidentsActions.LOAD_INCIDENTS({
          orderId: action.orderId,
          posId: action.posId,
          aliasFields: action.aliasFields
        });
      })
    );
  });
}
